import React, { useState, useCallback } from "react";
import { uploadImage } from "../../../utils/UploadImage";
import { toast } from "react-toastify";
import { useStaffMode } from "../../../hooks/StaffModeProvider";
import LegacyButton from "../../../components/Atomos/Buttons/LegacyButton";
import Image from "../../../components/Atomos/Icons/Image";

const ACCEPT =
  ".png,.jpg,.jpeg,.gif,image/png,image/jpeg,image/gif";
const MAX_SIZE_BYTES = 10 * 1024 * 1024; // 10MB

function validateFile(file: File): { valid: boolean; error?: string } {
  const isPNG =
    file.type === "image/png" || file.name.toLowerCase().endsWith(".png");
  const isJPG =
    file.type === "image/jpeg" ||
    file.name.toLowerCase().endsWith(".jpg") ||
    file.name.toLowerCase().endsWith(".jpeg");
  const isGIF =
    file.type === "image/gif" || file.name.toLowerCase().endsWith(".gif");

  if (!isPNG && !isJPG && !isGIF) {
    return { valid: false, error: "Only PNG, JPG, or GIF files are allowed" };
  }
  if (file.size > MAX_SIZE_BYTES) {
    return { valid: false, error: "File size must be less than 10MB" };
  }
  return { valid: true };
}

export type DragAndDropGalleryUploadProps = {
  eventId?: string;
  onPhotosAdded?: (urls: string[]) => void;
  onFilesAdded?: (files: File[]) => void;
  disabled?: boolean;
};

export default function DragAndDropGalleryUpload({
  eventId,
  onPhotosAdded,
  onFilesAdded,
  disabled = false,
}: DragAndDropGalleryUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const { isStaffMode } = useStaffMode();
  const deferUpload = Boolean(onFilesAdded);

  const processFiles = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0 || disabled) {
        return;
      }

      const validFiles: File[] = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const result = validateFile(file);
        if (result.valid) {
          validFiles.push(file);
        } else {
          toast.error(result.error);
        }
      }
      if (validFiles.length === 0) {
        return;
      }

      if (deferUpload && onFilesAdded) {
        onFilesAdded(validFiles);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }

      if (!eventId || !onPhotosAdded) {
        return;
      }
      setIsUploading(true);
      try {
        const sanitizeFileName = (fileName: string) =>
          fileName
            .toLowerCase()
            .replace(/[^a-z0-9.]/g, "-")
            .replace(/-+/g, "-")
            .replace(/^-|-$/g, "");

        const uploadPromises = validFiles.map((file) => {
          const sanitized = sanitizeFileName(file.name);
          const sanitizedFile = new File([file], sanitized, { type: file.type });
          return uploadImage(sanitizedFile, eventId, "events/");
        });

        const results = await Promise.all(uploadPromises);
        const urls = results.filter(
          (url): url is string => url !== undefined && url !== null
        );
        if (urls.length > 0) {
          onPhotosAdded(urls);
        }
        if (urls.length < validFiles.length) {
          toast.error("Some photos could not be uploaded");
        }
      } catch {
        toast.error("Error uploading photos");
      } finally {
        setIsUploading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    },
    [eventId, onPhotosAdded, onFilesAdded, disabled, deferUpload]
  );

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (disabled || isUploading) {
      return;
    }
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (disabled || isUploading) {
      return;
    }
    processFiles(e.dataTransfer.files);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    processFiles(e.target.files);
  };

  const handleAddClick = () => {
    if (disabled || isUploading) {
      return;
    }
    fileInputRef.current?.click();
  };

  return (
    <div className="w-full">
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT}
        multiple
        className="hidden"
        onChange={handleFileChange}
      />
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          disabled || isUploading
            ? "border-gray-200 bg-gray-50 cursor-not-allowed opacity-70"
            : isDragging
              ? "border-primary bg-primary/5"
              : "border-gray-300 bg-[#F4F4F4] hover:border-black/50"
        }`}
      >
        <div className="flex flex-col items-center gap-3">
        <div className="w-[3.75rem] h-[3.75rem] lg:w-[6rem] lg:h-[6rem] fill-gray-font">
           <Image/>
          </div>
          <div className="flex flex-col items-center gap-2 text-gray-font">
            <div className="flex flex-col items-center gap-1">
              <p>Drag and Drop your image here</p>
              <p>or</p>
              <p>Click 'Add Image' button to Browse</p>
            </div>
          <LegacyButton 
            content="text" 
            text="ADD IMAGE" 
            onClick={handleAddClick} 
            disabled={disabled || isUploading} 
            color={isStaffMode ? "accent" : "primary"} 
          />
          </div>
        </div>
      </div>
    </div>
  );
}
